import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Bot, Settings, Shield, Download, Cloud, Zap, CheckCircle, Smartphone } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Link } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const AndroidPage = ({ onBack, guides }) => {
  const navigate = useNavigate();
  // Function to count procedures in a guide by analyzing internal links
  const countProceduresInGuide = (guideId) => {
    const guide = guides?.find(g => g.id === guideId);
    if (!guide || !guide.content || !guide.content.sections) return 0;
    
    let linkCount = 0;
    guide.content.sections.forEach(section => {
      if (section.content) {
        // Count links in format [text](link-id)
        const links = section.content.match(/\[([^\]]+)\]\(([^)]+)\)/g);
        if (links) {
          linkCount += links.length;
        }
      }
    });
    return linkCount;
  };

  const androidCategories = [
    {
      id: 'android-setup',
      name: 'Configuration Android',
      icon: Settings,
      description: 'Configuration initiale, comptes Google et personnalisation',
      gradient: 'from-green-500 to-emerald-600',
      guideId: 'android-customization',
      hasGuide: true
    },
    {
      id: 'android-apps',
      name: 'Applications Android',
      icon: Download,
      description: 'Google Play Store, APKs et gestion des applications',
      gradient: 'from-blue-500 to-indigo-600',
      guideId: 'android-apps',
      hasGuide: false
    },
    {
      id: 'google-services',
      name: 'Services Google',
      icon: Cloud,
      description: 'Drive, Photos, Backup et synchronisation',
      gradient: 'from-cyan-500 to-blue-600',
      guideId: 'google-services',
      hasGuide: true
    },
    {
      id: 'android-security',
      name: 'Sécurité Android',
      icon: Shield,
      description: 'Biométrie, verrouillage et protection des données',
      gradient: 'from-red-500 to-pink-600',
      guideId: 'android-security',
      hasGuide: false
    },
    {
      id: 'android-productivity',
      name: 'Productivité Android',
      icon: Zap,
      description: 'Assistant Google, widgets et automatisation',
      gradient: 'from-orange-500 to-red-600',
      guideId: 'android-productivity',
      hasGuide: false
    },
    {
      id: 'android-customization',
      name: 'Personnalisation',
      icon: Bot,
      description: 'Launchers, thèmes et interface utilisateur',
      gradient: 'from-purple-500 to-indigo-600',
      guideId: 'android-ui-customization',
      hasGuide: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb />
        </div>
      </div>

      {/* Hero Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-3xl mb-8">
              <Bot className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
              Écosystème Android
            </h1>
            <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto">
              Personnalisez et optimisez votre smartphone Android avec nos guides pratiques
            </p>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-green-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-emerald-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-teal-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Android Guides Section */}
        <section className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl mb-6">
              <Smartphone className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Guides Android Pratiques
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Exploitez le potentiel de votre smartphone Android avec nos tutoriels détaillés
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {androidCategories.map((category, index) => {
              const IconComponent = category.icon;
              const procedureCount = category.hasGuide ? countProceduresInGuide(category.guideId) : 0;
              
              return (
                <Card 
                  key={index} 
                  className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-0 overflow-hidden relative h-full flex flex-col"
                  onClick={() => {
                    if (category.hasGuide) {
                      console.log('Android Guide selected:', { id: category.guideId });
                      navigate(`/mobile/guides/${category.guideId}`);
                    }
                  }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  
                  <CardHeader className="text-center pb-4 relative z-10 flex-grow">
                    <div className={`mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br ${category.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                      {category.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed flex-grow">
                      {category.description}
                    </CardDescription>
                    
                    {category.hasGuide && procedureCount > 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {procedureCount} procédure{procedureCount > 1 ? 's' : ''}
                        </Badge>
                      </div>
                    )}
                    
                    {category.hasGuide && procedureCount === 0 && (
                      <div className="mt-4 flex items-center justify-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          1 guide
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  
                  {/* Bottom Action Bar */}
                  <div className={`bg-gray-900 ${category.hasGuide ? `group-hover:bg-gradient-to-r group-hover:${category.gradient}` : ''} transition-all duration-300 p-4 relative z-10`}>
                    <Button 
                      variant="ghost"
                      disabled={!category.hasGuide}
                      className={`w-full font-bold text-lg hover:bg-transparent ${
                        category.hasGuide 
                          ? 'text-white' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {category.hasGuide ? 'Consulter le guide' : 'Bientôt disponible'}
                      {category.hasGuide && <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />}
                    </Button>
                  </div>
                  
                  {category.hasGuide && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <Link to="/mobile">
            <Button className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold px-8 py-4 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300">
              <ArrowLeft className="h-6 w-6 mr-3" />
              Retour aux appareils mobiles
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AndroidPage;